// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { EmailNotification } from "@modules/service/reducer/email-notifications/types";

import Step1Content from "./step1-content";
import Step2Reasons from "./step2-reasons";
import Step3Rules from "./step3-rules";
import StepAssignments from "./step-assignments";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3 | 4,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  list: Array<EmailNotification>,
  schema: ?Object,
  reasons: Array<AbsenceReason>,
  edited: ?EmailNotification,
  assignments: Array<HierarchyAssignment>,
  hierarchy: ?HierarchyTree,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getReasons: typeof sharedActionCreators.GetAbsenceReasons,
  setEdited: typeof actionCreators.SetEditedEmailNotification,
  setAssignments: typeof actionCreators.SetEmailNotificationAssignments,
  add: typeof actionCreators.AddEmailNotification,
  setShowModal: typeof actionCreators.SetShowAddEmailNotificationModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class EmailNotificationAddModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(this.props.schema);
    this.props.getReasons({ clientId: this.props.clientId });
  }

  get isValid() {
    return this.validate && this.validate(this.props.edited);
  }

  get isDefault() {
    return !this.props.list.length;
  }

  handleCancel = () => {
    this.props.setShowModal(false);
  };

  handleSubmit = () => {
    this.props.add({
      clientId: this.props.clientId,
      rule: this.props.edited,
      assignments: this.props.assignments,
    });
  };

  handleChange = ([step1, step2, step3, step4]: Array<Object> = []) => {
    this.props.setEdited({
      ...step1,
      ...step2,
      ...step3,
    });

    if (step4 && step4.assignments) {
      this.props.setAssignments(step4.assignments);
    }
  };

  get stepData() {
    const edited = this.props.edited || {};

    return [
      {
        data: {
          isActive: edited.isActive || true,
          name: edited.name,
          includeEmployeePhoneNumber: edited.includeEmployeePhoneNumber || false,
          includeWorkingHours: edited.includeWorkingHours || false,
          includeBradfordScore: edited.includeBradfordScore || false,
          includeJobTitle: edited.includeJobTitle || false,
          includeAbsenceId: edited.includeAbsenceId || false,
          includeAbsenceReason: edited.includeAbsenceReason || false,
          includeLastThreeAbsences: edited.includeLastThreeAbsences || false,
          includeDepartmentParent: edited.includeDepartmentParent || false,
          includeEmployeeRef: edited.includeEmployeeRef || false,
          includePosition: edited.includePosition || false,
          startInstruction: edited.startInstruction || undefined,
          updateNotification: edited.updateNotification || undefined,
          closedInstruction: edited.closedInstruction || undefined,
          overdueInstruction:
            edited.overdueInstruction || this.props.t("OVERDUE_ABSENCE_DEFAULT_TEXT"),
          reasonSpecificInstructions: edited.reasonSpecificInstructions || [],
          includeAlertsInAbsenceNotifications: edited.includeAlertsInAbsenceNotifications || false,
          includeRtwDetailsInAbsenceNotifications:
            edited.includeRtwDetailsInAbsenceNotifications || false,
          startNotifications: false,
        },
        valid: true,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EMAIL_NOTIFICATION_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          {this.props.schema ? (
            <LinearWizard
              defaultStep={this.props.defaultStep}
              data={this.stepData}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
            >
              <Step
                component={Step1Content}
                title={this.props.t("EMAIL_NOTIFICATION_STEP1")}
                schema={this.props.schema}
                edited={this.props.edited}
              />
              <Step
                component={Step2Reasons}
                title={this.props.t("EMAIL_NOTIFICATION_STEP2")}
                schema={this.props.schema}
                reasons={this.props.reasons}
                edited={this.props.edited}
              />
              <Step
                component={Step3Rules}
                title={this.props.t("EMAIL_NOTIFICATION_STEP3")}
                schema={this.props.schema}
                edited={this.props.edited}
                default={this.isDefault}
              />
              <Step
                component={StepAssignments}
                title={this.props.t("EMAIL_NOTIFICATION_STEP4")}
                hierarchy={this.props.hierarchy}
                assignments={this.props.assignments}
                hidden={this.isDefault}
              />
            </LinearWizard>
          ) : null}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="add_box"
            disabled={!(this.isValid && this.props.isAssignmentsValid)}
            onClick={this.handleSubmit}
          >
            {this.props.t("ADD")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    list: serviceSelectors.getEmailNotificationsList(state),
    schema: serviceSelectors.getEmailNotificationSchema(state),
    reasons: sharedSelectors.getAbsenceReasons(state), // ABSENCE REASONS
    edited: serviceSelectors.getEditedEmailNotification(state),
    assignments: serviceSelectors.getEmailNotificationAssignments(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    isAssignmentsValid: serviceSelectors.isEmailNotificationAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getReasons: sharedActionCreators.GetAbsenceReasons, // ABSENCE REASONS
  setEdited: actionCreators.SetEditedEmailNotification,
  setAssignments: actionCreators.SetEmailNotificationAssignments,
  add: actionCreators.AddEmailNotification,
  setShowModal: actionCreators.SetShowAddEmailNotificationModal,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmailNotificationAddModal);
